<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewWarrant', params: { id: id } }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Warrant">
        Warrant
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Payment Voucher Generation Form</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <form @submit.prevent="handleSubmit">
          <div class="crud-form position-relative">
            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center mb-4">
                <h1 class="mb-0">{{ formTitle }}</h1>
                <button type="button" class="btn btn-sm warrant-preview-btn" @click="openWarrantPreview">
                  View Warrant Details
                </button>
              </div>

              <div class="form-body">
                <!-- Top Section with Border -->
                <div class="top-section-border mb-4">
                  <div class="row mb-3">

                    <div class="col-md-12 form-group">
                      <label for="initiatedDate" class="form-label mb-2">Payment Voucher Number</label>
                      <input type="text" class="form-control" v-model="form.pvNumber" />
                    </div>
                    <!-- Beneficiary -->
                    <div class="col-md-6 form-group">
                      <div class="beneficiary-header mb-2">
                        <label class="form-label mb-0">Beneficiary</label>
                        <div class="checkbox-group">
                          <label class="checkbox-label">
                            <input type="checkbox" name="beneficiaryType" :checked="form.isMDA"
                              @change="onBeneficiaryTypeChange('mda')" id="mdaCheck">
                            <span>MDA</span>
                          </label>
                          <label class="checkbox-label">
                            <input type="checkbox" name="beneficiaryType" :checked="form.isConsultant"
                              @change="onBeneficiaryTypeChange('consultant')" id="consultantCheck">
                            <span style="white-space: nowrap;">Consultant/Contractor</span>
                          </label>
                        </div>
                      </div>

                      <input v-if="form.isMDA" type="text" class="form-control" :value="mdaInfo.name || ''" readonly>

                      <template v-else>
                        <SearchableSelect v-model="form.beneficiary" :options="consultantOptions"
                          :loading="loadingConsultantOptions" loading-text="Loading Consultants..."
                          placeholder="- Select Consultant/Contractor -" :required="form.isConsultant" />

                        <label class="form-label mb-1 small text-muted mt-2">Originating MDA</label>
                        <input type="text" class="form-control" :value="mdaInfo.name || ''" readonly>
                      </template>
                    </div>

                    <!-- Payment Type -->
                    <div class="col-md-6 form-group">
                      <label for="paymentType" class="form-label mb-2">Payment Type</label>
                      <select class="form-select payment-type-select" v-model="form.paymentType" required>
                        <option value="" disabled>- Select Payment Type -</option>
                        <option v-for="type in paymentTypes" :key="type.name" :value="type.name">{{ type.name }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="row mb-3" v-if="form.paymentType === 'Cheque'">
                    <!-- Cheque Number -->
                    <div class="col-md-6 form-group">
                      <label for="chequeNumber" class="form-label mb-2">Cheque Number</label>
                      <input id="chequeNumber" type="text" class="form-control" v-model="form.chequeNumber" required />
                    </div>
                  </div>

                  <div class="row mb-3">
                    <!-- Bank -->
                    <div class="col-md-6 form-group">
                      <label for="bank" class="form-label mb-2">Bank</label>
                      <select class="form-select" v-model="form.bank" :disabled="loadingBanks" required>
                        <option value="" disabled>{{ loadingBanks ? 'Loading Banks...' : '- Select Bank -' }}</option>
                        <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
                      </select>
                    </div>

                    <!-- Gross Amount -->
                    <div class="col-md-6 form-group">
                      <label for="grossAmount" class="form-label mb-2">Gross Amount</label>
                      <div class="amount-tooltip-wrapper">
                        <input id="grossAmount" type="text" class="form-control"
                          :value="formatCurrency(form.grossAmount)" inputmode="numeric" @keypress="onlyDigitsAndComma"
                          @input="onGrossAmountInput" placeholder="₦0.00" required />
                        <span class="amount-tooltip-text">PV Available Balance: {{ formatCurrency(amountLeft) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="row mb-3">
                    <!-- Net Amount -->
                    <div class="col-md-6 form-group">
                      <label for="netAmount" class="form-label mb-2">Net Amount</label>
                      <input type="text" class="form-control" :value="formatCurrency(netAmount)" readonly />
                    </div>

                    <!-- Initiated Date -->
                    <div class="col-md-6 form-group">
                      <label for="initiatedDate" class="form-label mb-2">Payment Date</label>
                      <input id="initiatedDate" type="date" class="form-control" v-model="form.initiatedDate" required />
                    </div>
                  </div>

                  <div class="row mb-3">
                    <!-- Particulars -->
                    <div class="col-md-12 form-group">
                      <label for="particulars" class="form-label mb-2">Particulars</label>
                      <textarea rows="3" class="form-control" v-model="form.particulars" readonly required></textarea>
                    </div>
                  </div>

                  <div class="row mb-3">
                    <!-- First Level Approver -->
                    <div class="col-md-6 form-group">
                      <label for="firstLevelApproverId" class="form-label mb-2">First Level Approver</label>
                      <select class="form-select" v-model="form.firstLevelApproverId" :disabled="loadingFirstApprovals" required>
                        <option value="" disabled>{{ loadingFirstApprovals ? 'Loading First Level Approvers...' : '- Select First Level Approver -' }}</option>
                        <option v-for="approval in firstApprovals" :key="approval.id" :value="approval.id">{{ approval.role }} - {{ approval.name }}</option>
                      </select>
                    </div>

                    <!-- Second Level Approver -->
                    <div class="col-md-6 form-group">
                      <label for="secondLevelApproverId" class="form-label mb-2">Second Level Approver</label>
                      <select class="form-select" v-model="form.secondLevelApproverId" :disabled="loadingSecondApprovals" required>
                        <option value="" disabled>{{ loadingSecondApprovals ? 'Loading Second Level Approvers ...' : '- Select Second Level Approver -' }}</option>
                        <option v-for="approval in secondApprovals" :key="approval.id" :value="approval.id">{{ approval.role }} - {{ approval.name }}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Deductions Table -->
                <div class="mb-4">
                  <div class="table-responsive">
                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-header">
                        <tr>
                          <th>Description</th>
                          <th>Percentage</th>
                          <th>Amount</th>
                          <th style="width: 110px;">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="deductions.length === 0">
                          <td colspan="4" class="text-muted">No deductions added</td>
                        </tr>
                        <tr v-for="(deduction, index) in deductions" :key="index"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td data-label="Description">
                            {{ deduction.description === 'Other' ? deduction.description + ' - ' + deduction.name : (deduction.description || deduction.name) }}
                          </td>
                          <td data-label="Percentage">
                            {{ deduction.inputMode === 'percentage' ? `${Number(deduction.percentage || 0).toFixed(2)}%` : '-' }}
                          </td>
                          <td data-label="Amount">{{ formatCurrency(deduction.amount) }}</td>
                          <td class="deduction-actions">
                            <button type="button" class="btn btn-outline-secondary btn-sm"
                              @click="openDeductionModalForEdit(index)">
                              <span class="iconify" data-icon="mdi:pencil-outline"></span>
                            </button>
                            <button type="button" class="btn btn-outline-danger btn-sm" @click="removeDeduction(index)">
                              <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Add Deduction Section -->
                  <div class="add-section">
                    <p class="section-title mb-3 text-center">Select Deduction to Add:</p>
                    <div class="add-controls-wrapper">
                      <div class="add-controls">
                        <div class="filter-group">
                          <label class="control-label">Filter Options</label>
                          <input type="text" v-model="deductionSearchTerm" placeholder="Search title/code"
                            class="form-control form-control-sm" />
                        </div>

                        <div class="select-group">
                          <label class="control-label">Choose Deductions</label>
                          <select class="form-select form-select-sm" v-model="selectedDeductionId"
                            :disabled="loadingDeductionOptions">
                            <option value="" disabled>
                              {{ loadingDeductionOptions ? 'Loading deductions...' : '-- pick deduction --' }}</option>
                            <option v-for="opt in filteredDeductionOptions" :key="opt.id" :value="opt.id">
                              {{ opt.name }}
                            </option>
                            <option>Other</option>
                          </select>
                        </div>

                        <div class="button-group">
                          <label class="control-label">&nbsp;</label>
                          <button type="button" class="btn btn-outline-primary btn-sm"
                            :disabled="!selectedDeductionId || loadingDeductionOptions"
                            @click="openDeductionModalForCreate">
                            <span class="iconify" data-icon="mdi:plus"></span>
                            Configure & Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- MDA Section -->
                <div class="mda-section mb-4">
                  <div class="warrant-header mb-3">
                    <h5 class="mda-title mb-0 text-start">Warrant No: <span class="warrant-no">{{ loadingWarrantData ?
                      'Loading...' : (warrantInfo.number ||
                        'N/A') }}</span></h5>
                    <h5 class="mda-title mb-0 text-end">{{ loadingWarrantData ? 'Loading MDA...' : (mdaInfo.name ||
                      'N/A') }}</h5>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-header">
                        <tr>
                          <th>Eco Code</th>
                          <th>Eco Line Item</th>
                          <th>Amount Budgeted</th>
                          <th>Amount Released</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="loadingWarrantData">
                          <td colspan="4" class="text-muted">Loading warrant economic items...</td>
                        </tr>
                        <tr v-else-if="mdaEconomicItems.length === 0">
                          <td colspan="4" class="text-muted">No economic items available</td>
                        </tr>
                        <tr v-for="(item, index) in mdaEconomicItems" :key="index"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td data-label="Eco Code">{{ item.ecoCode }}</td>
                          <td data-label="Eco Line Item">{{ item.ecoLineItem }}</td>
                          <td data-label="Amount Budgeted">{{ formatCurrency(item.amountBudgeted) }}</td>
                          <td data-label="Amount Released">{{ formatCurrency(item.amountReleased) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="d-flex justify-content-center mt-4">
                  <button type="submit" class="btn btn-submit" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    {{ loading ? 'Submitting...' : submitLabel }}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showWarrantPreview" class="warrant-preview-overlay" @click="closeWarrantPreview">
      <div class="warrant-preview-modal" @click.stop>
        <div class="warrant-preview-header">
          <h5 class="mb-0">Warrant Details</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeWarrantPreview"></button>
        </div>

        <div class="warrant-preview-body">
          <div class="preview-grid mb-3">
            <div class="preview-item">
              <small>Warrant Number</small>
              <p class="mb-0">{{ warrantPreview.number || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Reference Number</small>
              <p class="mb-0">{{ warrantPreview.refNumber || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Issue Date</small>
              <p class="mb-0">{{ formatDisplayDate(warrantPreview.issueDate) }}</p>
            </div>
            <div class="preview-item">
              <small>MDA</small>
              <p class="mb-0">{{ warrantPreview.mda || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Org Code</small>
              <p class="mb-0">{{ warrantPreview.orgCode || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Geo Code</small>
              <p class="mb-0">{{ warrantPreview.geoCode || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Function Code</small>
              <p class="mb-0">{{ warrantPreview.funcCode || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Fund Code</small>
              <p class="mb-0">{{ warrantPreview.fundCode || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Program Code</small>
              <p class="mb-0">{{ warrantPreview.programCode || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Amount Released</small>
              <p class="mb-0">{{ formatCurrency(warrantPreview.amountReleased) || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Payment Voucher Available Balance (₦)</small>
              <p class="mb-0">{{ formatCurrency(warrantPreview.pvAvailableBalance) || 'N/A' }}</p>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-bordered text-center align-middle">
              <thead class="table-header">
                <tr>
                  <th>Eco Code</th>
                  <th>Eco Line Item</th>
                  <th>Amount Budgeted</th>
                  <th>Amount Released</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="mdaEconomicItems.length === 0">
                  <td colspan="4" class="text-muted">No economic items available</td>
                </tr>
                <tr v-for="(item, index) in mdaEconomicItems" :key="`preview-${index}`">
                  <td>{{ item.ecoCode }}</td>
                  <td>{{ item.ecoLineItem }}</td>
                  <td>{{ formatCurrency(item.amountBudgeted) }}</td>
                  <td>{{ formatCurrency(item.amountReleased) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDeductionModal" class="deduction-modal-overlay" @click="closeDeductionModal">
      <div class="deduction-modal" @click.stop>
        <div class="deduction-modal-header">
          <h5 v-if="!isOther" class="mb-0">{{ deductionModalMode === 'edit' ? 'Edit Deduction' : 'Add Deduction' }}</h5>
          <!-- Other Deduction -->
          <h5 v-if="isOther" class="mb-0">{{ deductionModalMode === 'edit' ? 'Edit Other Deduction' : 'Create Other Deduction' }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeDeductionModal"></button>
        </div>

        <div class="deduction-modal-body">
          <div v-if="!isOther" class="deduction-selected mb-3">
            <small>Deduction Type</small>
            <p class="mb-0">{{ deductionDraft.name || 'N/A' }}</p>
          </div>
          <!-- Other Deduction -->
          <div v-if="isOther" class="deduction-selected mb-3">
            <small>Deduction Type</small>
            <p class="mb-1">Other</p>
            <small>Deduction Name</small>
            <div>
              <input type="text" class="form-control" v-model="deductionDraft.name" required />
            </div>
            <small>Deduction Description</small>
            <div>
              <textarea type="text" class="form-control" v-model="deductionDraft.description" required></textarea>
            </div>
          </div>

          <div v-if="!isOther" class="mb-3">
            <!-- <label class="form-label mb-2">How do you want to enter this deduction?</label> -->
            <div class="deduction-input-mode">
              <label class="checkbox-label" :class="{ 'mode-disabled': !isPercentageModeDeduction }">
                <input type="radio" value="percentage" v-model="deductionDraft.inputMode"
                  :disabled="!isPercentageModeDeduction" @change="enforceDeductionInputMode">
                <span>Percentage (%)</span>
              </label>
              <label class="checkbox-label" :class="{ 'mode-disabled': isPercentageModeDeduction }">
                <input type="radio" value="amount" v-model="deductionDraft.inputMode"
                  :disabled="isPercentageModeDeduction" @change="enforceDeductionInputMode">
                <span>Fixed Amount (₦)</span>
              </label>
            </div>
          </div>
          <!-- Other Deduction -->
          <div v-if="isOther" class="mb-3">
            <!-- <label class="form-label mb-2">How do you want to enter this deduction?</label> -->
            <div class="deduction-input-mode">
              <label class="checkbox-label">
                <input type="radio" value="percentage" v-model="deductionDraft.inputMode"
                  @change="enforceDeductionInputMode">
                <span>Percentage (%)</span>
              </label>
              <label class="checkbox-label">
                <input type="radio" value="amount" v-model="deductionDraft.inputMode"
                  @change="enforceDeductionInputMode">
                <span>Fixed Amount (₦)</span>
              </label>
            </div>
          </div>

          <div v-if="!isOther" class="mb-3">
            <label class="form-label mb-2">{{ deductionDraft.inputMode === 'percentage' ? 'Percentage' : 'Amount' }}</label>
            <input v-if="deductionDraft.inputMode === 'percentage'" type="number" class="form-control"
              v-model.number="deductionDraft.value" min="0" max="100" step="0.01" inputmode="decimal"
              @keydown="preventNonNumericInput" placeholder="Enter percentage" />
            <input v-else type="number" class="form-control" v-model.number="deductionDraft.value" min="0"
              :max="form.grossAmount" step="0.01" inputmode="decimal" @keydown="preventNonNumericInput"
              placeholder="Enter amount" />
          </div>
          <!-- Other Deduction -->
          <div v-if="isOther" class="mb-3">
            <label class="form-label mb-2">{{ deductionDraft.inputMode === 'percentage' ? 'Percentage' : 'Amount' }}</label>
            <input v-if="deductionDraft.inputMode === 'percentage'" type="number" class="form-control"
              v-model.number="deductionDraft.value" min="0" max="100" step="0.01" inputmode="decimal"
              @keydown="preventNonNumericInput" placeholder="Enter percentage" />
            <input v-else type="number" class="form-control" v-model.number="deductionDraft.value" min="0"
              :max="form.grossAmount" step="0.01" inputmode="decimal" @keydown="preventNonNumericInput"
              placeholder="Enter amount" />
              <div class="form-check mt-2">
                <input class="form-check-input" type="checkbox" v-model="deductionDraft.fedInd" id="fedInd">
                <label class="form-check-label" for="fedInd">
                  Federal Deduction
                </label>
              </div>
          </div>

          <div class="deduction-preview-grid">
            <div class="deduction-preview-item">
              <small>Gross Amount</small>
              <p class="mb-0">{{ formatCurrency(form.grossAmount) }}</p>
            </div>
            <div class="deduction-preview-item">
              <small>Computed Percentage</small>
              <p class="mb-0">{{ Number(modalCalculatedPercentage).toFixed(2) }}%</p>
            </div>
            <div class="deduction-preview-item">
              <small>Computed Amount</small>
              <p class="mb-0">{{ formatCurrency(modalCalculatedAmount) }}</p>
            </div>
          </div>

          <p class="deduction-hint mt-3 mb-0">
            Tip: Percentage is always calculated from the current gross amount.
          </p>
        </div>

        <div class="deduction-modal-footer">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeDeductionModal">Cancel</button>
          <button v-if="!isOther" type="button" class="btn btn-outline-primary btn-sm"
            :disabled="Number(deductionDraft.value || 0) <= 0" @click="saveDeductionFromModal">
            {{ deductionModalMode === 'edit' ? 'Save Changes' : 'Add Deduction' }}
          </button>
          <!-- Other Deduction -->
          <button v-if="isOther" type="button" class="btn btn-outline-primary btn-sm"
            :disabled="Number(deductionDraft.value || 0) <= 0" @click="saveDeductionFromModal">
            {{ deductionModalMode === 'edit' ? 'Save Changes' : 'Add New Deduction' }}
          </button>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import SearchableSelect from '../common/SearchableSelect.vue'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const warrantRouteParam = computed(() => route.params.warrantId ? String(route.params.warrantId) : '')
const pvRefFromQuery = computed(() => {
  if (route.query.pvId) return String(route.query.pvId)
  if (route.query.pvNumber) return String(route.query.pvNumber)
  return null
})
const isEditMode = computed(() => Boolean(pvRefFromQuery.value))

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const loading = ref(false)
const loadingBanks = ref(false)
const loadingMDAOptions = ref(false)
const loadingConsultantOptions = ref(false)
const loadingDeductionOptions = ref(false)
const loadingWarrantData = ref(false)
const loadingFirstApprovals = ref(false)
const loadingSecondApprovals = ref(false)
const showWarrantPreview = ref(false)
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
const existingPvPayload = ref(null)

const form = reactive({
  isMDA: true,
  isConsultant: false,
  beneficiary: '',
  payee: '',
  paymentType: '',
  chequeNumber: '',
  bank: '',
  grossAmount: 0,
  initiatedDate: '',
  particulars: '',
  natureOfExpenditure: '',
  firstLevelApproverId: '',
  secondLevelApproverId: '',
  pvNumber: ''
})

const beneficiaries = ref([])
const consultantOptions = ref([])
const paymentTypes = ref([
  { name: 'Transfer' },
  { name: 'Cheque' }
])
const banks = ref([])
const deductions = ref([])
const deductionOptions = ref([])
const selectedDeductionId = ref('')
const deductionSearchTerm = ref('')
const showDeductionModal = ref(false)
const isOther = computed(() => selectedDeductionId.value === 'Other')
const deductionModalMode = ref('create')
const editingDeductionIndex = ref(null)
const deductionDraft = reactive({
  id: '',
  name: '',
  description: '',
  dedTypeAmt: 0,
  dedTypeMode: 0,
  dedSetByUserInd: 0,
  dedPercentByUser: 0,
  inputMode: 'percentage',
  value: 0,
  fedInd: 0,
})

const mdaInfo = reactive({
  id: '',
  name: ''
})

const warrantInfo = reactive({
  number: ''
})

const warrantPreview = reactive({
  number: '',
  refNumber: '',
  issueDate: '',
  mda: '',
  orgCode: '',
  geoCode: '',
  funcCode: '',
  fundCode: '',
  programCode: '',
  amountReleased: 0,
  pvAvailableBalance: 0
})

const mdaEconomicItems = ref([])
const firstApprovals = ref([])
const secondApprovals = ref([])

const filteredDeductionOptions = computed(() =>
  deductionOptions.value.filter(item =>
    item.name.toLowerCase().includes(deductionSearchTerm.value.toLowerCase())
  )
)

const formTitle = computed(() => isEditMode.value ? 'Edit Returned Payment Voucher' : 'Payment Voucher Generation Form')
const submitLabel = computed(() => isEditMode.value ? 'Update Voucher' : 'Submit')

const totalDeductions = computed(() =>
  deductions.value.reduce((sum, ded) => sum + (ded.amount || 0), 0)
)

const totalAmountBudgeted = computed(() =>
  mdaEconomicItems.value.reduce((sum, item) => sum + Number(item.amountBudgeted || 0), 0)
)

const netAmount = computed(() =>
  Math.max(0, form.grossAmount - totalDeductions.value)
)

const initiatedDateMonth = computed(() => {
  const parts = String(form.initiatedDate || '').split('-')
  return parts.length >= 2 ? Number(parts[1]) : 0
})

const initiatedDateYear = computed(() => {
  const parts = String(form.initiatedDate || '').split('-')
  return parts.length >= 1 ? Number(parts[0]) : 0
})

const amountLeft = computed(() => {
  const availableBalance = Number(warrantPreview.pvAvailableBalance || 0)
  const budgetedBalance = Number(totalAmountBudgeted.value || 0)
  return Math.max(0, Math.min(availableBalance, budgetedBalance) - Number(form.grossAmount || 0))
})

const modalCalculatedAmount = computed(() => {
  const value = Number(deductionDraft.value || 0)
  if (value <= 0) return 0

  if (deductionDraft.inputMode === 'percentage') {
    return Number(((form.grossAmount * value) / 100).toFixed(2))
  }

  return Number(value.toFixed(2))
})

const modalCalculatedPercentage = computed(() => {
  const amount = Number(modalCalculatedAmount.value || 0)
  if (amount <= 0 || form.grossAmount <= 0) return 0
  return Number(((amount / form.grossAmount) * 100).toFixed(2))
})

const isPercentageModeDeduction = computed(() => Number(deductionDraft.dedTypeMode || 0) === 1)

const enforceDeductionInputMode = () => {
if (isOther.value) {
    deductionDraft.dedTypeMode = deductionDraft.inputMode === 'percentage' ? 1 : 0
    return
  }
    deductionDraft.inputMode = isPercentageModeDeduction.value ? 'percentage' : 'amount'
}

const onBeneficiaryTypeChange = (type) => {
  if (type === 'mda') {
    form.isMDA = true
    form.isConsultant = false
    form.beneficiary = ''
    form.payee = mdaInfo.name || ''
    return
  }

  form.isMDA = false
  form.isConsultant = true
  form.beneficiary = ''
  form.payee = ''
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return ''
  const num = Number(val)
  if (isNaN(num)) return ''
  return '₦' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const withCurrentTime = (dateStr) => {
  const now = new Date()
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')
  return `${dateStr} ${hh}:${mm}:${ss}`
}

const formatDisplayDate = (val) => {
  if (!val) return 'N/A'
  const date = new Date(val)
  if (Number.isNaN(date.getTime())) return String(val)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const onlyDigitsAndComma = (e) => {
  const k = e.key
  const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End']
  if ((k >= '0' && k <= '9') || allowed.includes(k)) return
  e.preventDefault()
}

const preventNonNumericInput = (e) => {
  const key = e.key
  const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End', '.']
  if ((key >= '0' && key <= '9') || allowed.includes(key)) {
    if (key !== '.') return

    const currentValue = String(e.target?.value || '')
    if (currentValue.includes('.')) e.preventDefault()
    return
  }

  e.preventDefault()
}

const onGrossAmountInput = (e) => {
  const raw = (e.target.value || '').replace(/\D/g, '')
  if (raw === '') {
    form.grossAmount = 0
    e.target.value = ''
    recalculateDeductionsForGrossAmount()
    return
  }
  const num = parseInt(raw, 10) / 100
  form.grossAmount = Number(num.toFixed(2))
  e.target.value = formatCurrency(form.grossAmount)
  recalculateDeductionsForGrossAmount()
}

const recalculateDeductionsForGrossAmount = () => {
  deductions.value = deductions.value.map(deduction => {
    if (deduction.inputMode === 'percentage') {
      const amount = (form.grossAmount * Number(deduction.inputValue || 0)) / 100
      return {
        ...deduction,
        percentage: Number(deduction.inputValue || 0),
        amount: Number(amount.toFixed(2))
      }
    }

    const amount = Number(deduction.inputValue || 0)
    const percentage = form.grossAmount > 0 ? (amount / form.grossAmount) * 100 : 0

    return {
      ...deduction,
      amount: Number(amount.toFixed(2)),
      percentage: Number(percentage.toFixed(2))
    }
  })
}

async function createOtherDeduction() {
  loadingDeductionOptions.value = true
  const token = Cookies.get('auth_token')

  if (Number(deductionDraft.value || 0) <= 0) return

  const enteredValue = Number(deductionDraft.value || 0)
  const grossAmount = Number(form.grossAmount || 0)
  const activeInputMode = Number(deductionDraft.dedTypeMode || 0) === 1 ? 'percentage' : 'amount'

  if (activeInputMode === 'percentage' && enteredValue > 100) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Deduction percentage cannot be more than 100%.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const attemptedAmount = activeInputMode === 'percentage'
    ? Number(((grossAmount * enteredValue) / 100).toFixed(2))
    : enteredValue

  if (attemptedAmount > grossAmount) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Deduction amount cannot be greater than Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const dedPayload = {
    id: '',
    name: deductionDraft.name,
    description: deductionDraft.description,
    mode: Number(deductionDraft.dedTypeMode || 0),
    fedInd: Number(deductionDraft.fedInd || 0),
    defaultValue: Number(deductionDraft.value || 0),
    activeInd: 1,
    errorMessage: '',
    paymentType: '',
  }
  try {
    await axios.post(`${root_api_url}/payment/deduction-type`, dedPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
  } catch (error) {
      console.error('Failed to create deduction other:', error)
      Swal.fire({
        html: `${iconHtmlError}
          <h1 style="font-weight:600;">Error</h1>
          <p>${error.response?.data?.errorMessage || "Failed to create deduction"}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
      return
  } finally {
    loadingDeductionOptions.value = false
  }
}

async function fetchOtherDeduction(name) {
  loadingDeductionOptions.value = true
  const token = Cookies.get('auth_token')

  try {
    const response = await axios.get(`${root_api_url}/payment/deduction-type/name/${name}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    const otherDeduction = response.data
    if (otherDeduction) {
      deductionDraft.id = otherDeduction.id || ''
      deductionDraft.name = otherDeduction.name || ''
      deductionDraft.description = otherDeduction.description || ''
      deductionDraft.dedTypeAmt = Number(otherDeduction.defaultValue || 0)
      deductionDraft.dedTypeMode = Number(otherDeduction.mode || 0)
      deductionDraft.dedSetByUserInd = 0
      deductionDraft.dedPercentByUser = 0
      deductionDraft.inputMode = Number(otherDeduction.mode || 0) === 1 ? 'percentage' : 'amount'
      deductionDraft.value = Number(otherDeduction.defaultValue || 0)
      deductionDraft.fedInd = Number(otherDeduction.fedInd || 0)
    }
  } catch (error) {
    console.error('Failed to fetch other deduction:', error)
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error.response?.data?.errorMessage || "Failed to fetch other deduction"}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loadingDeductionOptions.value = false
  }
}

const openDeductionModalForCreate = () => {
  if (Number(form.grossAmount || 0) <= 0) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Please enter Gross Amount before adding deductions.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      returnFocus: false,
      icon: undefined,
    }).then(() => {
      const grossAmountField = document.getElementById('grossAmount')
      if (grossAmountField) {
        grossAmountField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        grossAmountField.focus()
      }
    })
    return
  }
  if (loadingDeductionOptions.value) return

  const selected = deductionOptions.value.find(d => d.id === selectedDeductionId.value) || "Other"
  if (!selected) return

  const exists = deductions.value.some(d => d.id === selected.id)
  if (exists) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>This deduction is already added</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  deductionModalMode.value = 'create'
  editingDeductionIndex.value = null
  const selectedDedTypeMode = Number(selected.dedTypeMode ?? 0)
  const selectedDedTypeAmt = Number(selected.dedTypeAmt ?? selected.defaultValue ?? selected.percentage ?? 0)

  if (isOther.value) {
    deductionDraft.id = ''
    deductionDraft.name = ''
    deductionDraft.description = ''
    deductionDraft.fedInd = Number(selected.fedInd ?? 0)
  } else {
    deductionDraft.id = selected.id
    deductionDraft.name = selected.name
    deductionDraft.description = selected.description || selected.name
  }
  deductionDraft.dedTypeAmt = selectedDedTypeAmt
  deductionDraft.dedTypeMode = selectedDedTypeMode
  deductionDraft.dedSetByUserInd = 0
  deductionDraft.dedPercentByUser = 0
  deductionDraft.inputMode = selectedDedTypeMode === 1 ? 'percentage' : 'amount'
  deductionDraft.value = selectedDedTypeAmt
  showDeductionModal.value = true
}

const openDeductionModalForEdit = (index) => {
  const selected = deductions.value[index]
  if (!selected) return

  deductionModalMode.value = 'edit'
  editingDeductionIndex.value = index
  const selectedDedTypeMode = Number(selected.dedTypeMode ?? 0)
  const selectedDedTypeAmt = Number(selected.dedTypeAmt ?? selected.defaultValue ?? selected.percentage ?? 0)
  deductionDraft.id = selected.id
  deductionDraft.name = selected.name
  deductionDraft.description = selected.description || selected.name
  deductionDraft.dedTypeAmt = selectedDedTypeAmt
  deductionDraft.dedTypeMode = selectedDedTypeMode
  deductionDraft.dedSetByUserInd = Number(selected.dedSetByUserInd ?? 0)
  deductionDraft.dedPercentByUser = Number(selected.dedPercentByUser ?? 0)
  deductionDraft.inputMode = selectedDedTypeMode === 1 ? 'percentage' : 'amount'
  deductionDraft.value = Number(selected.inputValue ?? selectedDedTypeAmt)
  showDeductionModal.value = true
}

const closeDeductionModal = () => {
  showDeductionModal.value = false
}

const saveDeductionFromModal = async () => {
  if (isOther.value) {
    await createOtherDeduction()
    await fetchOtherDeduction(deductionDraft.name)
  }

  if (Number(deductionDraft.value || 0) <= 0) return

  const enteredValue = Number(deductionDraft.value || 0)
  const grossAmount = Number(form.grossAmount || 0)
  const activeInputMode = Number(deductionDraft.dedTypeMode || 0) === 1 ? 'percentage' : 'amount'

  if (activeInputMode === 'percentage' && enteredValue > 100) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Deduction percentage cannot be more than 100%.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const attemptedAmount = activeInputMode === 'percentage'
    ? Number(((grossAmount * enteredValue) / 100).toFixed(2))
    : enteredValue

  if (attemptedAmount > grossAmount) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Deduction amount cannot be greater than Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const existingDeductionAmount = deductionModalMode.value === 'edit' && editingDeductionIndex.value !== null
    ? Number(deductions.value[editingDeductionIndex.value]?.amount || 0)
    : 0
  const projectedTotalDeductions = Number(totalDeductions.value || 0) - existingDeductionAmount + attemptedAmount

  if (projectedTotalDeductions > grossAmount) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Total deductions cannot exceed Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const normalizedInputMode = Number(deductionDraft.dedTypeMode || 0) === 1 ? 'percentage' : 'amount'
  const normalizedInputValue = Number(deductionDraft.value || 0)
  const computedAmount = normalizedInputMode === 'percentage'
    ? Number(((form.grossAmount * normalizedInputValue) / 100).toFixed(2))
    : Number(normalizedInputValue.toFixed(2))
  const computedPercentage = form.grossAmount > 0
    ? Number(((computedAmount / form.grossAmount) * 100).toFixed(2))
    : 0
  const defaultPercentage = Number(deductionDraft.dedTypeAmt || 0)
  const dedSetByUserInd = normalizedInputMode === 'percentage' && Math.abs(normalizedInputValue - defaultPercentage) > 0.000001 ? 1 : 0
  const dedPercentByUser = dedSetByUserInd ? normalizedInputValue : 0

  const nextDeduction = {
    id: deductionDraft.id,
    name: deductionDraft.name,
    description: deductionDraft.description || deductionDraft.name,
    dedTypeAmt: Number(deductionDraft.dedTypeAmt || 0),
    dedTypeMode: Number(deductionDraft.dedTypeMode || 0),
    dedSetByUserInd,
    dedPercentByUser,
    inputMode: normalizedInputMode,
    inputValue: normalizedInputValue,
    percentage: computedPercentage,
    amount: computedAmount
  }

  if (deductionModalMode.value === 'edit' && editingDeductionIndex.value !== null) {
    deductions.value.splice(editingDeductionIndex.value, 1, nextDeduction)
  } else {
    deductions.value.push(nextDeduction)
  }

  selectedDeductionId.value = ''
  closeDeductionModal()
}

const removeDeduction = (index) => {
  deductions.value.splice(index, 1)
}

const openWarrantPreview = () => {
  showWarrantPreview.value = true
}

const closeWarrantPreview = () => {
  showWarrantPreview.value = false
}

const selectedMDA = computed(() =>
  beneficiaries.value.find(mda => String(mda.id) === String(form.beneficiary))
)

const selectedConsultant = computed(() =>
  consultantOptions.value.find(consultant => String(consultant.id) === String(form.beneficiary))
)

const selectedBank = computed(() =>
  banks.value.find(bank => String(bank.id) === String(form.bank))
)

watch(
  () => [form.isMDA, form.isConsultant, form.beneficiary, mdaInfo.name],
  () => {
    if (form.isConsultant) {
      form.payee = selectedConsultant.value?.name || ''
      return
    }

    if (form.isMDA) {
      form.payee = mdaInfo.name || selectedMDA.value?.name || ''
      return
    }

    form.payee = ''
  },
  { immediate: true }
)

const mappedDeductions = computed(() =>
  deductions.value.map(deduction => ({
    dedTypeId: deduction.id,
    dedTypeName: deduction.name,
    dedTypeAmt: Number(deduction.dedTypeAmt ?? 0),
    dedTypeMode: Number(deduction.dedTypeMode ?? 0),
    dedSetByUserInd: Number(deduction.dedSetByUserInd ?? 0),
    dedPercentByUser: Number(deduction.dedPercentByUser ?? 0),
    amount: Number(deduction.amount || 0)
  }))
)

const mappedDeductionsForUpdate = computed(() => {
  const existingDeductions = Array.isArray(existingPvPayload.value?.deductions)
    ? existingPvPayload.value.deductions
    : []

  return deductions.value.map(deduction => {
    const matchedExistingDeduction = existingDeductions.find(item =>
      String(item?.dedTypeId || item?.id || '') === String(deduction.id)
    )

    return {
      id: matchedExistingDeduction?.id || null,
      createdBy: matchedExistingDeduction?.createdBy || existingPvPayload.value?.createdBy || null,
      lastModifiedBy: existingPvPayload.value?.lastModifiedBy || matchedExistingDeduction?.lastModifiedBy || null,
      userId: matchedExistingDeduction?.userId || existingPvPayload.value?.userId || null,
      dedTypeId: deduction.id,
      dedTypeName: deduction.name,
      dedTypeAmt: Number(deduction.dedTypeAmt ?? matchedExistingDeduction?.dedTypeAmt ?? 0),
      dedTypeMode: Number(deduction.dedTypeMode ?? matchedExistingDeduction?.dedTypeMode ?? 0),
      dedSetByUserInd: Number(deduction.dedSetByUserInd ?? matchedExistingDeduction?.dedSetByUserInd ?? 0),
      dedPercentByUser: Number(deduction.dedPercentByUser ?? matchedExistingDeduction?.dedPercentByUser ?? 0),
      paymentVoucherId: matchedExistingDeduction?.paymentVoucherId || existingPvPayload.value?.id || null,
      amount: Number(deduction.amount || 0),
      newEntity: matchedExistingDeduction?.newEntity ?? false,
      version: Number(matchedExistingDeduction?.version ?? 0),
      createdDate: matchedExistingDeduction?.createdDate || null,
      lastModifiedDate: matchedExistingDeduction?.lastModifiedDate || null
    }
  })
})

const mapExistingDeductions = (source) => {
  deductions.value = (source || []).map(item => {
    const dedTypeMode = Number(item?.dedTypeMode ?? item?.mode ?? 0)
    const dedTypeAmt = Number(item?.dedTypeAmt ?? item?.defaultValue ?? 0)
    const amount = Number(item?.amount || 0)
    const percentage = Number(item?.percentage ?? (form.grossAmount > 0 ? ((amount / form.grossAmount) * 100) : 0))
    const description = item?.dedTypeName || item?.description || item?.name || 'Deduction'

    return {
      id: item?.dedTypeId || item?.id || '',
      name: item?.dedTypeName || item?.name || description,
      description,
      dedTypeAmt,
      dedTypeMode,
      dedSetByUserInd: Number(item?.dedSetByUserInd ?? 0),
      dedPercentByUser: Number(item?.dedPercentByUser ?? 0),
      inputMode: dedTypeMode === 1 ? 'percentage' : 'amount',
      inputValue: dedTypeMode === 1 ? Number(item?.inputValue ?? percentage ?? dedTypeAmt) : Number(item?.inputValue ?? amount),
      percentage,
      amount
    }
  })
}

const hydrateWarrantDisplayFromPv = (pv) => {
  if (!pv) return

  if (pv.mdaId) mdaInfo.id = pv.mdaId
  if (pv.mdaName) mdaInfo.name = pv.mdaName

  warrantInfo.number = pv.warrantNumber || pv.warrantNo || warrantInfo.number || ''

  warrantPreview.number = pv.warrantNumber || pv.warrantNo || warrantPreview.number || ''
  warrantPreview.mda = pv.mdaName || warrantPreview.mda || ''
  warrantPreview.pvAvailableBalance = Number(pv.payVoucherBalance ?? pv.pvAvailableBalance ?? warrantPreview.pvAvailableBalance ?? 0)
}

const fetchExistingPaymentVoucher = async () => {
  if (!pvRefFromQuery.value) return

  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/payment/voucher/${pvRefFromQuery.value}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    existingPvPayload.value = data
    hydrateWarrantDisplayFromPv(data)
    form.paymentType = data?.paymentType ? String(data.paymentType).charAt(0).toUpperCase() + String(data.paymentType).slice(1).toLowerCase() : ''
    form.bank = data?.bankId || ''
    form.grossAmount = Number(data?.grossAmount || 0)
    form.initiatedDate = data?.paymentDate ? String(data.paymentDate).slice(0, 10) : form.initiatedDate
    form.particulars = data?.particulars || ''
    form.natureOfExpenditure = data?.natureOfExpenditure || ''
    form.payee = data?.payee || ''
    form.chequeNumber = data?.chequeNumber || ''

    const payee = String(data?.payee || '').trim().toLowerCase()
    const mdaName = String(data?.mdaName || mdaInfo.name || '').trim().toLowerCase()
    const consultant = consultantOptions.value.find(item => String(item?.name || '').trim().toLowerCase() === payee)

    if (consultant && payee && payee !== mdaName) {
      form.isMDA = false
      form.isConsultant = true
      form.beneficiary = consultant.id
    } else {
      form.isMDA = true
      form.isConsultant = false
      form.beneficiary = data?.mdaId || ''
    }

    mapExistingDeductions(data?.deductions || [])

    const pvWarrantId = data?.warrantId || data?.warrantUUID || data?.warrantUuid || data?.warrantID || ''
    if (pvWarrantId) {
      await fetchWarrantData(String(pvWarrantId), false)
    }
  } catch (error) {
    console.error('Failed to load existing payment voucher for correction:', error)
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to load returned payment voucher for correction.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  }
}

const fetchWarrantData = async (warrantRef = warrantRouteParam.value, applyParticulars = true) => {
  if (!warrantRef) {
    console.error('No warrant ID provided for PV with warrant page')
    return
  }

  loadingWarrantData.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/appraisal/warrant/${warrantRef}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    mdaInfo.id = data.mdaId || data.mdaUUID || data.mdaUuid || ''
    mdaInfo.name = data.mdaName || data.mda || ''
    warrantInfo.number = data.warrantNumber || data.warrantNo || ''
    warrantPreview.number = data.warrantNumber || data.warrantNo || ''
    warrantPreview.refNumber = data.refNumber || ''
    warrantPreview.issueDate = data.issueDate || ''
    warrantPreview.mda = data.mdaName || data.mda || ''
    warrantPreview.orgCode = data.orgCode || ''
    warrantPreview.geoCode = data.geoCode || ''
    warrantPreview.funcCode = data.funcCode || ''
    warrantPreview.fundCode = data.fundCode || ''
    warrantPreview.programCode = data.programCode || ''
    warrantPreview.amountReleased = Number(data.amountReleased || 0)
    warrantPreview.pvAvailableBalance = Number(data.payVoucherBalance ?? data.pvAvailableBalance ?? 0)
    form.natureOfExpenditure = data.natureOfExpenditure || ''
    if (applyParticulars) {
      form.particulars = data.details || ''
    }
    mdaEconomicItems.value = (data.warrantEconomicLineItems || []).map(item => ({
      id: item.id,
      ecoCode: item.ecoCode,
      ecoLineItem: item.title,
      mdaLineItemBudgetId: item.mdaLineItemBudgetId,
      amountBudgeted: item.amountBudgeted,
      amountReleased: item.amountReleased,
      balance: item.balance,
      amountRequired: item.amountRequired,
      year: item.year,
      quarter: item.quarter,
      amountRecommended: item.amountRecommended || 0
    }))

  } catch (error) {
    console.error('Failed to fetch warrant data:', error)
  } finally {
    loadingWarrantData.value = false
  }
}

const fetchDeductionOptions = async () => {
  loadingDeductionOptions.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/deduction-type`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    deductionOptions.value = data
      .filter(ded => Number(ded.activeInd ?? 1) !== 0)
      .map(ded => ({
        id: ded.id,
        name: ded.deductionName || ded.name || '',
        dedTypeAmt: Number(ded.dedTypeAmt ?? ded.defaultValue ?? ded.percentage ?? 0),
        dedTypeMode: Number(ded.dedTypeMode ?? ded.mode ?? 0),
        percentage: Number(ded.defaultValue ?? ded.percentage ?? 0),
        description: ded.description || ''
      }))

  } catch (error) {
    deductionOptions.value = []
    console.error('Failed to fetch deduction options:', error)
  } finally {
    loadingDeductionOptions.value = false
  }
}

const fetchConsultantOptions = async () => {
  loadingConsultantOptions.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/consultant/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    consultantOptions.value = data.map(consultant => ({
      id: consultant.id ?? consultant.name,
      name: consultant.name || ''
    }))
  } catch (error) {
    consultantOptions.value = []
    console.error('Failed to fetch consultants:', error)
  } finally {
    loadingConsultantOptions.value = false
  }
}

const fetchBanks = async () => {
  loadingBanks.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/banks`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    banks.value = data.map(bank => ({
      id: bank.id,
      name: bank.bankName || ''
    }))
  } catch (error) {
    banks.value = []
    console.error('Failed to fetch banks:', error)
  } finally {
    loadingBanks.value = false
  }
}

const fetchMDAOptions = async () => {
  loadingMDAOptions.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    beneficiaries.value = data.map(mda => ({
      id: mda.id,
      name: mda.name || ''
    }))
  } catch (error) {
    beneficiaries.value = []
    console.error('Failed to fetch MDAs:', error)
  } finally {
    loadingMDAOptions.value = false
  }
}

const fetchFirstApprovals = async () => {
  loadingFirstApprovals.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/app_level/1`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    firstApprovals.value = data.map(approver => ({
      id: approver.userId,
      name: approver.fullName || '',
      role: approver.roleName || '',
    }))

  } catch (error) {
    firstApprovals.value = []
    console.error('Failed to fetch first approvers:', error)
  } finally {
    loadingFirstApprovals.value = false
  }
}

const fetchSecondApprovals = async () => {
  loadingSecondApprovals.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/app_level/2`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    secondApprovals.value = data.map(approver => ({
      id: approver.userId,
      name: approver.fullName || '',
      role: approver.roleName || '',
    }))
  } catch (error) {
    secondApprovals.value = []
    console.error('Failed to fetch second approvers:', error)
  } finally {
    loadingSecondApprovals.value = false
  }
}


const handleSubmit = async () => {
  const token = Cookies.get('auth_token')

  if (Number(form.grossAmount || 0) > Number(totalAmountBudgeted.value || 0)) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Gross Amount cannot exceed the Amount Budgeted (${formatCurrency(totalAmountBudgeted.value)}).</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  if (Number(form.grossAmount || 0) > Number(warrantPreview.pvAvailableBalance || 0)) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Gross Amount cannot exceed the Payment Voucher Available Balance (${formatCurrency(warrantPreview.pvAvailableBalance)}).</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  if (Number(totalDeductions.value || 0) > Number(form.grossAmount || 0)) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Total deductions cannot exceed Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  if (form.paymentType === 'Cheque' && !form.chequeNumber) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Kindly enter the Cheque Number.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const paymentData = {
    payee: form.payee || (form.isConsultant ? (selectedConsultant.value?.name || '') : (selectedMDA.value?.name || mdaInfo.name || '')),
    bankId: form.bank,
    bankName: selectedBank.value?.name || '',
    mdaId: mdaInfo.id || selectedMDA.value?.id || null,
    grossAmount: Number(form.grossAmount || 0),
    deductions: mappedDeductions.value,
    netAmount: Number(netAmount.value || 0),
    paymentDate: form.initiatedDate,
    pvInitDate: withCurrentTime(form.initiatedDate),
    pvMonth: initiatedDateMonth.value,
    pvYear: initiatedDateYear.value,
    paymentType: form.paymentType.toLowerCase(),
    particulars: form.particulars,
    natureOfExpenditure: form.natureOfExpenditure,
    chequeNumber: form.paymentType === 'Cheque' ? form.chequeNumber : null,
    warrantId: existingPvPayload.value?.warrantId || warrantRouteParam.value || null,
    warrantNumber: existingPvPayload.value?.warrantNumber || warrantInfo.number || '',
    mdaName: mdaInfo.name || '',
    firstLevelApproverId: form.firstLevelApproverId || '',
    secondLevelApproverId: form.secondLevelApproverId || '',
    pvNumber: form.pvNumber,
  }

  const existing = existingPvPayload.value || {}

  const updatePayload = {
    id: existing.id || null,
    createdBy: existing.createdBy || null,
    lastModifiedBy: existing.lastModifiedBy || null,
    userId: existing.userId || null,
    pvNumber: existing.pvNumber || '',
    payee: paymentData.payee,
    mdaId: paymentData.mdaId,
    mdaName: mdaInfo.name || existing.mdaName || '',
    bankId: paymentData.bankId,
    bankName: paymentData.bankName,
    grossAmount: paymentData.grossAmount,
    deductions: mappedDeductionsForUpdate.value,
    netAmount: paymentData.netAmount,
    paymentDate: paymentData.paymentDate,
    warrantId: paymentData.warrantId,
    warrantNumber: paymentData.warrantNumber,
    memoId: existing.memoId || null,
    pvStatus: existing.pvStatus || '',
    errorMessage: existing.errorMessage || '',
    pvInitDate: existing.pvInitDate || null,
    pvInitiatorId: existing.pvInitiatorId || null,
    ticketId: existing.ticketId || null,
    standardPVInd: Number(existing.standardPVInd ?? 1),
    paymentType: paymentData.paymentType,
    particulars: paymentData.particulars,
    chequeNumber: paymentData.chequeNumber,
    pvMonth: Number(existing.pvMonth ?? 0),
    pvYear: Number(existing.pvYear ?? 0),
    natureOfExpenditure: existing.natureOfExpenditure || '',
    standardPV: Boolean(existing.standardPV ?? true),
    newEntity: false,
    version: Number(existing.version ?? 0),
    createdDate: existing.createdDate || null,
    lastModifiedDate: existing.lastModifiedDate || null,
    firstLevelApproverId: paymentData.firstLevelApproverId || '',
    secondLevelApproverId: paymentData.secondLevelApproverId || '',
  }

  loading.value = true

  try {
    const pvRefForUpdate = existing.id || pvRefFromQuery.value

    if (isEditMode.value && pvRefForUpdate) {

      await axios.put(`${root_api_url}/payment/voucher/${pvRefForUpdate}`, updatePayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Returned payment voucher updated and re-submitted for approval.'
      }).then(() => {
        router.push({ name: 'ViewPaymentVouchers' })
      })

    } else {

      await axios.post(`${root_api_url}/payment/voucher/temp`, paymentData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Payment Voucher Generated Successfully'
      }).then(() => {
        router.push({ name: 'ViewPaymentVouchers' })
      })

      // await axios.post(`${root_api_url}/payment/voucher/standard-pv`, paymentData, {
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     'Content-Type': 'application/json',
      //   },
      // })

      // Swal.fire({
      //   icon: 'success',
      //   title: 'Success',
      //   text: 'Payment Voucher Generated Successfully'
      // }).then(() => {
      //   router.push({ name: 'ViewPaymentVouchers' })
      // })
    }

  } catch (error) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to generate payment voucher'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await Promise.all([
    fetchBanks(),
    fetchMDAOptions(),
    fetchConsultantOptions(),
    fetchDeductionOptions(),
    fetchFirstApprovals(),
    fetchSecondApprovals()
  ])

  if (isEditMode.value) {
    await fetchExistingPaymentVoucher()
  } else {
    await fetchWarrantData(warrantRouteParam.value)
  }

  document.title = isEditMode.value ? 'Edit Returned Payment Voucher' : 'Payment Voucher Generation'
})
</script>

<style scoped>
/* Top Section Border */
.top-section-border {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 1.5rem;
  background-color: #ffffff;
  margin-bottom: 1.5rem;
}

/* Beneficiary Header Layout */
.beneficiary-header {
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 1rem;
}

.beneficiary-header .form-label {
  white-space: nowrap;
}

/* Checkbox Group Styling */
.checkbox-group {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin: 0;
  padding-top: 5px;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin: 0;
  font-size: 0.9rem;
}

.checkbox-label input[type="radio"],
.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0;
}

.checkbox-label span {
  user-select: none;
  white-space: nowrap !important;
}

/* Table Styling */
.table-header th {
  background-color: #f3f4f6;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
  padding: 0.75rem;
  font-size: 0.9rem;
}

.table tbody td {
  vertical-align: middle;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
}

.deduction-actions {
  display: flex;
  gap: 0.4rem;
  justify-content: center;
}

/* Add Section Styling */
.add-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 4px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
}

.add-controls-wrapper {
  display: flex;
  justify-content: center;
}

.add-controls {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: nowrap;
}

.filter-group,
.select-group,
.button-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group {
  flex: 0 0 160px;
}

.select-group {
  flex: 0 0 220px;
}

.button-group {
  flex: 0 0 auto;
}

.button-group .control-label {
  visibility: hidden;
  height: 1.4rem;
}

.control-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

/* MDA Section */
.mda-section {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.mda-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0;
}

.warrant-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.warrant-no {
  font-weight: 700;
}

/* Submit Button */
.btn-submit {
  background-color: #10b981;
  border-color: #10b981;
  color: #ffffff;
  padding: 0.625rem 3rem;
  font-weight: 500;
  border-radius: 4px;
  font-size: 1rem;
}

.btn-submit:hover {
  background-color: #059669;
  border-color: #059669;
  color: #ffffff;
}

.btn-submit:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}

/* Form Control Enhancements */
.form-control:focus,
.form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

.form-control[readonly] {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

/* Mobile Card Layout for Tables */
@media (max-width: 768px) {
  .table-responsive {
    border: none;
  }

  .table thead {
    display: none;
  }

  .table tbody {
    display: block;
  }

  .table tbody tr {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .table tbody td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border: none;
    border-bottom: 1px solid #f3f4f6;
    text-align: left !important;
  }

  .table tbody td:last-child {
    border-bottom: none;
    justify-content: center;
  }

  .table tbody td::before {
    content: attr(data-label);
    font-weight: 600;
    color: #374151;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .table tbody td:last-child::before {
    content: '';
  }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .beneficiary-header {
    flex-wrap: wrap;
  }

  .checkbox-group {
    gap: 1rem;
  }

  .add-controls-wrapper {
    justify-content: stretch;
  }

  .add-controls {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .filter-group,
  .select-group,
  .button-group {
    flex: 1 1 100%;
    width: 100%;
  }

  .button-group {
    align-items: stretch;
    margin-top: 0.5rem;
  }

  .button-group .control-label {
    display: none;
  }

  .button-group .btn {
    width: 100%;
  }

}

.payment-type-select {
  margin-top: 5px !important;
}

/* Gross Amount Tooltip */
.amount-tooltip-wrapper {
  position: relative;
}

.amount-tooltip-text {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background-color: #1f2937;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  padding: 0.4rem 0.65rem;
  border-radius: 4px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 10;
}

.amount-tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: #1f2937 transparent transparent transparent;
}

.amount-tooltip-wrapper:hover .amount-tooltip-text,
.amount-tooltip-wrapper:focus-within .amount-tooltip-text {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.warrant-preview-btn {
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d1d5db;
  font-weight: 600;
}

.warrant-preview-btn:hover {
  background: #f3f4f6;
  color: #111827;
  border-color: #9ca3af;
}

.warrant-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.deduction-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 1rem;
}

.deduction-modal {
  background: #ffffff;
  width: min(560px, 100%);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.deduction-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.85rem 1rem;
}

.deduction-modal-body {
  padding: 1rem;
}

.deduction-modal-footer {
  border-top: 1px solid #e5e7eb;
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.deduction-selected {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.deduction-selected small {
  color: #6b7280;
  font-size: 0.75rem;
}

.deduction-selected p {
  color: #111827;
  font-weight: 600;
}

.deduction-input-mode {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.deduction-input-mode .mode-disabled {
  color: #9ca3af;
  opacity: 0.7;
}

.deduction-input-mode .mode-disabled span {
  color: #9ca3af;
}

.deduction-preview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
}

.deduction-preview-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.deduction-preview-item small {
  color: #6b7280;
  font-size: 0.75rem;
}

.deduction-preview-item p {
  color: #111827;
  font-weight: 600;
}

.deduction-hint {
  color: #6b7280;
  font-size: 0.82rem;
}

.warrant-preview-modal {
  background: #ffffff;
  width: min(1000px, 100%);
  max-height: 85vh;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.warrant-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.warrant-preview-body {
  padding: 1rem;
  overflow: auto;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.preview-item {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.preview-item small {
  display: block;
  color: #6b7280;
  font-size: 0.75rem;
  margin-bottom: 0.2rem;
}

.preview-item p {
  color: #111827;
  font-weight: 600;
  word-break: break-word;
}

.preview-item-wide {
  grid-column: span 3;
}

@media (max-width: 992px) {
  .preview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .preview-item-wide {
    grid-column: span 2;
  }
}

@media (max-width: 576px) {
  .preview-grid {
    grid-template-columns: 1fr;
  }

  .preview-item-wide {
    grid-column: span 1;
  }

  .deduction-preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
